/**
 * Tipos para transcrição de áudio/vídeo
 */

/**
 * Resposta quando a análise não está disponível (ainda processando)
 */
export interface AnalysisNotAvailable {
  available: false
  message: string
}

/**
 * Verifica se a resposta é do tipo AnalysisNotAvailable
 */
export function isAnalysisNotAvailable(data: any): data is AnalysisNotAvailable {
  return data && typeof data === 'object' && data.available === false && typeof data.message === 'string'
}

export interface TranscriptSegment {
  speaker: string
  text: string
  start_time: string
  end_time: string
  confidence: number
}

export interface Transcription {
  meeting_id: string
  title: string
  date: string
  duration: string
  transcript_source: string
  participants: string[]
  transcript: TranscriptSegment[]
  full_text: string
  topics: string[]
  /** Mapeamento de nomes originais de speakers para nomes personalizados */
  speakerMapping?: Record<string, string>
}

export interface TranscriptionMetrics {
  totalDuration: number
  totalWords: number
  totalSegments: number
  averageSegmentDuration: number
  averageWordsPerSegment: number
  speakerCount: number
  speakerStats: SpeakerStat[]
  wordsPerMinute: number
}

export interface SpeakerStat {
  speaker: string
  segmentCount: number
  totalDuration: number
  wordCount: number
  percentage: number
}

export interface LegalTerm {
  term: string
  category: string
  description: string
  occurrences: TermOccurrence[]
}

export interface TermOccurrence {
  segmentIndex: number
  startTime: string
  text: string
}

export interface SearchResult {
  segmentIndex: number
  segment: TranscriptSegment
  matchedText: string
  matchStart: number
  matchEnd: number
}

export interface AISummary {
  overview: string
  keyPoints: string[]
  actionItems: string[]
  decisions: string[]
  participants: string[]
  duration: number
  mainTopic: string
  context: string
}

// ===== FASE 2: Análise Avançada =====

/**
 * Análise de sentimento por segmento
 */
export type SentimentLabel = 'positive' | 'neutral' | 'negative'

export interface SegmentSentiment {
  segmentIndex: number
  speaker: string
  text: string
  startTime: string
  sentiment: SentimentLabel
  score: number // -1 a 1
  confidence: number // 0 a 1
  keywords: string[] // palavras que influenciaram o sentimento
}

export interface SentimentAnalysis {
  overall: {
    positive: number // percentual
    neutral: number
    negative: number
    averageScore: number
  }
  segments: SegmentSentiment[]
  timeline: {
    time: string
    score: number
  }[]
}

/**
 * Extração de entidades
 */
export type EntityType = 'PERSON' | 'ORGANIZATION' | 'LOCATION' | 'DATE' | 'EVIDENCE' | 'LEGAL_TERM' | 'MONEY'

export interface Entity {
  id: string
  type: EntityType
  value: string
  normalizedValue?: string
  occurrences: {
    segmentIndex: number
    startTime: string
    context: string
  }[]
  count: number
  relevance: number // 0 a 1
}

export interface EntityExtraction {
  entities: Entity[]
  byType: Record<EntityType, Entity[]>
  relationships: {
    entity1: string
    entity2: string
    relationship: string
    context: string
  }[]
}

/**
 * Detecção de tópicos
 */
export interface Topic {
  id: string
  name: string
  description: string
  startTime: string
  endTime: string
  duration: number // em segundos
  keywords: string[]
  segments: number[] // índices dos segmentos
  confidence: number
  color: string
}

export interface TopicDetection {
  topics: Topic[]
  transitions: {
    fromTopic: string
    toTopic: string
    timestamp: string
    transitionType: 'smooth' | 'abrupt'
  }[]
  dominantTopic: string
  topicDistribution: {
    topicId: string
    percentage: number
  }[]
}

// ===== FASE 3: Funcionalidades Avançadas =====

/**
 * Timeline de Eventos - Eventos extraídos da transcrição
 */
export type EventCategory = 'action' | 'statement' | 'evidence' | 'decision' | 'threat' | 'agreement'

export interface TimelineEvent {
  id: string
  timestamp: string // timestamp do segmento
  mentionedTime?: string // tempo mencionado no texto (ex: "2 semanas atrás")
  category: EventCategory
  title: string
  description: string
  participants: string[]
  segmentIndex: number
  importance: 'high' | 'medium' | 'low'
  relatedEvents?: string[] // IDs de eventos relacionados
}

export interface EventTimeline {
  events: TimelineEvent[]
  eventsByCategory: Record<EventCategory, TimelineEvent[]>
  chronologicalOrder: string[] // IDs em ordem cronológica
  keyMoments: TimelineEvent[] // eventos mais importantes
}

/**
 * Análise de Padrões de Fala
 */
export type SpeechPatternType = 
  | 'hesitation' 
  | 'interruption' 
  | 'repetition' 
  | 'negation' 
  | 'emphasis' 
  | 'uncertainty' 
  | 'aggression'

export interface SpeechPattern {
  id: string
  type: SpeechPatternType
  text: string
  segmentIndex: number
  startTime: string
  speaker: string
  context: string
  significance: string
}

export interface SpeechPatternAnalysis {
  patterns: SpeechPattern[]
  byType: Record<SpeechPatternType, SpeechPattern[]>
  bySpeaker: Record<string, SpeechPattern[]>
  statistics: {
    totalPatterns: number
    mostCommonType: SpeechPatternType
    speakerWithMostPatterns: string
    patternsPerMinute: number
  }
  insights: string[]
}

/**
 * Comparação de Transcrições
 */
export type DiffType = 'added' | 'removed' | 'modified' | 'unchanged'

export interface SegmentDiff {
  segmentIndex: number
  originalText: string
  comparedText: string
  diffType: DiffType
  changes: {
    type: 'word' | 'phrase'
    original: string
    compared: string
    position: number
  }[]
  similarity: number // 0 a 1
}

export interface TranscriptionComparison {
  originalId: string
  comparedId: string
  originalTitle: string
  comparedTitle: string
  overallSimilarity: number
  segments: SegmentDiff[]
  statistics: {
    totalSegments: number
    addedSegments: number
    removedSegments: number
    modifiedSegments: number
    unchangedSegments: number
  }
  summary: string
}

/**
 * Workflow de Revisão Colaborativa
 */
export type ReviewStatus = 'pending' | 'in_review' | 'approved' | 'rejected' | 'needs_revision'
export type CommentType = 'comment' | 'suggestion' | 'correction' | 'question' | 'approval'

export interface Reviewer {
  id: string
  name: string
  email: string
  avatar?: string
  role: 'owner' | 'reviewer' | 'viewer'
}

export interface ReviewComment {
  id: string
  segmentIndex: number
  startTime: string
  author: Reviewer
  type: CommentType
  text: string
  createdAt: string
  updatedAt?: string
  resolved: boolean
  replies: ReviewComment[]
  highlightedText?: string
}

export interface ReviewTask {
  id: string
  title: string
  description: string
  assignee: Reviewer
  status: 'todo' | 'in_progress' | 'done'
  dueDate?: string
  segmentRange?: { start: number; end: number }
  createdAt: string
}

export interface CollaborativeReview {
  transcriptionId: string
  status: ReviewStatus
  owner: Reviewer
  reviewers: Reviewer[]
  comments: ReviewComment[]
  tasks: ReviewTask[]
  history: {
    action: string
    user: Reviewer
    timestamp: string
    details?: string
  }[]
  currentVersion: number
  lastUpdated: string
  approvals: {
    reviewer: Reviewer
    approved: boolean
    timestamp: string
    notes?: string
  }[]
}

/**
 * Comparação Múltipla de Transcrições
 */
export interface MultiComparisonFile {
  fileId: string
  title: string
  duration: string
  speakers: string[]
  segmentCount: number
  wordCount: number
  createdAt: string
}

export interface SimilarityPair {
  fileId1: string
  fileId2: string
  similarity: number
  matchingSegments: number
  commonSpeakers: string[]
  divergentTopics: string[]
}

export interface MatchingSegment {
  fileId: string
  segmentIndex: number
  text: string
  speaker: string
  startTime: string
  similarity: number
}

export interface Contradiction {
  id: string
  topic: string
  severity: 'high' | 'medium' | 'low'
  segments: {
    fileId: string
    fileTitle: string
    segmentIndex: number
    speaker: string
    text: string
    startTime: string
  }[]
  description: string
}

export interface MultiTranscriptionComparison {
  files: MultiComparisonFile[]
  similarityMatrix: SimilarityPair[]
  overallSimilarity: number
  matchingSegments: MatchingSegment[][]
  contradictions: Contradiction[]
  commonTopics: string[]
  uniqueTopicsByFile: Record<string, string[]>
  speakerMapping: Record<string, Record<string, string>> // fileId -> originalSpeaker -> mappedName
  statistics: {
    totalSegments: number
    matchingCount: number
    contradictionCount: number
    averageSimilarity: number
  }
  generatedAt: string
}

/**
 * Anotações do Usuário
 */
export type AnnotationColor = 'yellow' | 'green' | 'blue' | 'red' | 'purple'
export type AnnotationCategory = 'note' | 'question' | 'important' | 'todo' | 'reference'

export interface Annotation {
  id: string
  segmentIndex: number
  startTime: string
  text: string // Texto da anotação
  segmentText: string // Trecho do segmento anotado
  color: AnnotationColor
  category: AnnotationCategory
  createdAt: number
  updatedAt: number
}

export interface SegmentHighlight {
  id: string
  segmentIndex: number
  startTime: string
  color: AnnotationColor
  label?: string
  createdAt: number
}

export interface Bookmark {
  id: string
  segmentIndex: number
  startTime: string
  title: string
  description?: string
  createdAt: number
}

export interface UserAnnotations {
  items: Annotation[]
  highlights: SegmentHighlight[]
  bookmarks: Bookmark[]
}

// ===== Edição de Segmentos =====

/** Requisição de edição de um segmento (enviada ao backend) */
export interface SegmentEditRequest {
  segmentIndex: number
  text?: string
  speaker?: string
}

/** Resposta do backend ao salvar edições */
export interface SegmentEditResponse {
  status: string
  message: string
  editedCount: number
}

/** Registro de auditoria de uma edição (retornado pelo backend) */
export interface SegmentEditRecord {
  id: string
  segmentIndex: number
  originalText: string
  originalSpeaker: string
  newText: string
  newSpeaker: string
  editedBy: string
  editedAt: number
}
